import React, { useCallback, useEffect, useState } from 'react';
import {
  getTeamLeaderClassScheduleSectionsPreview,
  type TeamLeaderClassScheduleImportPayload,
  type TeamLeaderClassScheduleSectionPreviewRow,
} from '../../services/classScheduleTeamLeaderImportService';
import type { ClassScheduleRow, WeekdayKey } from '../../services/sectionLayoutService';

interface ClassScheduleLeaderSectionImportModalProps {
  open: boolean;
  teamId: string;
  targetWorkspaceId: string;
  targetSectionId: string;
  onClose: () => void;
  onImported: (payload: TeamLeaderClassScheduleImportPayload) => void | Promise<void>;
}

const WEEKDAY_COLUMNS: Array<{ key: WeekdayKey; label: string }> = [
  { key: 'mon', label: '월' },
  { key: 'tue', label: '화' },
  { key: 'wed', label: '수' },
  { key: 'thu', label: '목' },
  { key: 'fri', label: '금' },
];

function parseClassName(raw: string): { title: string; grades: number[] } {
  const [title, rest] = raw.split('::');
  if (!rest) return { title: raw, grades: [] };
  const grades = rest
    .split(',')
    .map((v) => Number(v))
    .filter((n) => Number.isInteger(n) && n >= 1 && n <= 6);
  return { title, grades };
}

function rowKindLabel(kind: ClassScheduleRow['kind']): string {
  if (kind === 'activity') return '활동';
  if (kind === 'after_school') return '방과후';
  return '수업';
}

function formatCell(value: string): string {
  return value.trim() || '-';
}

function ClassScheduleLeaderSectionCard({ section }: { section: TeamLeaderClassScheduleSectionPreviewRow }) {
  return (
    <article className="cp-card cp-class-schedule-leader-import-section-card is-selected">
      <div className="cp-track-section-header cp-track-section-header-with-class cp-grade-track-header-row cp-class-schedule-leader-import-section-head">
        <div className="card-summary pill cp-track-class-pill">클래스 공유</div>
        <h3>수업시간 및 요일별 기준 수업시수 설정</h3>
        <div className="cp-class-schedule-leader-import-section-classes">
          {section.grade_labels.length === 0 ? (
            <span className="cp-academic-leader-import-empty-class">(연결 클래스 없음)</span>
          ) : (
            section.grade_labels.map((label, index) => {
              const parsed = parseClassName(label);
              return (
                <span key={`${label}-${index}`} className="cp-grade-track-class-block">
                  <span className="cp-grade-track-class-block-label">{parsed.title}</span>
                  {parsed.grades.map((grade) => (
                    <span key={grade} className="class-grade-chip-display">
                      {grade}
                    </span>
                  ))}
                </span>
              );
            })
          )}
        </div>
      </div>

      <div className="cp-class-schedule-leader-import-preview">
        <article className="cp-card cp-class-schedule-table-card">
          <div className="cp-class-schedule-table-wrap">
            <table className="cp-class-schedule-table cp-class-schedule-unified-table cp-class-schedule-leader-import-table">
              <thead>
                <tr>
                  <th colSpan={6} className="cp-period-table-title-cell">
                    하루 일과 및 수업시간
                  </th>
                  <th colSpan={6} className="cp-period-table-divider cp-period-table-title-cell">
                    요일별 기준 수업시수
                  </th>
                </tr>
                <tr>
                  <th>순</th>
                  <th>구분</th>
                  <th>이름</th>
                  <th>시작시각</th>
                  <th>운영시간(분)</th>
                  <th>종료시각</th>
                  <th className="cp-period-table-divider">구분</th>
                  {WEEKDAY_COLUMNS.map((col) => (
                    <th key={col.key}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {section.rows.map((row, idx) => {
                  const classCountUpToHere = section.rows
                    .slice(0, idx + 1)
                    .filter((r) => r.kind === 'class').length;
                  const periodLabel = row.kind === 'class' ? `${classCountUpToHere}교시` : '';
                  const rightLabel =
                    row.kind === 'class' ? periodLabel : row.kind === 'activity' ? '활동' : row.name || '방과후';
                  const period = idx + 1;
                  return (
                    <tr
                      key={`${section.section_id}-${idx}`}
                      className={
                        row.kind === 'activity'
                          ? 'cp-class-schedule-row-activity'
                          : row.kind === 'after_school'
                            ? 'cp-class-schedule-row-after-school'
                            : ''
                      }
                    >
                      <td className="cp-class-schedule-seq">
                        <div>{idx + 1}</div>
                        {periodLabel ? <div className="cp-class-schedule-period-label">{periodLabel}</div> : null}
                      </td>
                      <td>{rowKindLabel(row.kind)}</td>
                      <td className="cp-class-schedule-leader-import-text-cell">{formatCell(row.name)}</td>
                      <td>{formatCell(row.startTime)}</td>
                      <td>{formatCell(row.durationMinutes)}</td>
                      <td>{formatCell(row.endTime)}</td>
                      <td className="cp-period-table-divider cp-period-right-label">{rightLabel}</td>
                      {WEEKDAY_COLUMNS.map((col) => {
                        const checked = row.kind === 'class' && period <= section.weekday_max[col.key];
                        return (
                          <td key={`${section.section_id}-${col.key}-${period}`}>
                            <span className={`cp-period-toggle-static${checked ? ' is-checked' : ''}`}>
                              {checked ? 'v' : ''}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </article>
      </div>
    </article>
  );
}

const ClassScheduleLeaderSectionImportModal: React.FC<ClassScheduleLeaderSectionImportModalProps> = ({
  open,
  teamId,
  onClose,
  onImported,
}) => {
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sections, setSections] = useState<TeamLeaderClassScheduleSectionPreviewRow[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (open) setActiveIndex(0);
  }, [open]);

  useEffect(() => {
    setActiveIndex((i) => {
      if (sections.length === 0) return 0;
      return Math.min(Math.max(0, i), sections.length - 1);
    });
  }, [sections]);

  const loadPreview = useCallback(async () => {
    if (!open || !teamId) return;
    setLoading(true);
    setError(null);
    try {
      const payload = await getTeamLeaderClassScheduleSectionsPreview(teamId);
      setSections(payload.sections);
      setActiveIndex(0);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
      setSections([]);
    } finally {
      setLoading(false);
    }
  }, [open, teamId]);

  useEffect(() => {
    void loadPreview();
  }, [loadPreview]);

  const selectedId = sections[activeIndex]?.section_id ?? null;
  const sectionCount = sections.length;
  const navDisabled = sectionCount <= 1;

  const handleImport = async () => {
    const selected = sections[activeIndex];
    if (!selected) return;
    setImporting(true);
    setError(null);
    try {
      /** DB 즉시 저장 RPC 대신 미리보기 페이로드만 넘김 — 페이지에서 저장하기로 반영 */
      await onImported({ rows: selected.rows, weekday_max: selected.weekday_max });
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setImporting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="cp-modal-backdrop cp-class-schedule-leader-import-backdrop" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="cp-modal cp-class-schedule-leader-import-modal" role="dialog" aria-modal="true" aria-labelledby="cp-class-schedule-leader-import-title" onMouseDown={(e) => e.stopPropagation()}>
        <div className="cp-leader-import-modal-title-row">
          <button
            type="button"
            className="cp-leader-import-modal-nav-btn"
            aria-label="이전 섹션"
            disabled={navDisabled || activeIndex <= 0}
            onClick={() => setActiveIndex((i) => Math.max(0, i - 1))}
          >
            &lt;
          </button>
          <h3 id="cp-class-schedule-leader-import-title" className="cp-leader-import-modal-title-text">
            팀장 수업시간 설정 가져오기
          </h3>
          <button
            type="button"
            className="cp-leader-import-modal-nav-btn"
            aria-label="다음 섹션"
            disabled={navDisabled || activeIndex >= sectionCount - 1}
            onClick={() => setActiveIndex((i) => Math.min(sectionCount - 1, i + 1))}
          >
            &gt;
          </button>
        </div>
        <p className="page-desc cp-class-schedule-leader-import-intro">
          타이틀 양쪽 화살표로 팀장 섹션을 넘긴 뒤 가져오기를 누르면, 현재 섹션에 팀장 수업시간 및 요일별 기준 수업시수가 적용됩니다.
          화면에는 바로 반영되며, 오른쪽 위 &quot;저장하기&quot;를 눌러야 서버에 저장됩니다.
        </p>
        {error ? <p className="cp-grade-track-leader-import-error">{error}</p> : null}
        <div className="cp-class-schedule-leader-import-body">
          {loading ? (
            <p className="page-desc">불러오는 중...</p>
          ) : sections.length === 0 ? (
            <p className="page-desc">팀장 쪽에 표시할 섹션이 없습니다.</p>
          ) : (
            <div className="cp-class-schedule-leader-import-section-list cp-leader-import-section-single">
              {sections[activeIndex] ? (
                <ClassScheduleLeaderSectionCard key={sections[activeIndex].section_id} section={sections[activeIndex]} />
              ) : null}
            </div>
          )}
        </div>
        <div className="cp-modal-footer">
          <button type="button" className="cp-btn-secondary" onClick={onClose} disabled={importing}>
            닫기
          </button>
          <button
            type="button"
            className="cp-btn-primary"
            onClick={() => void handleImport()}
            disabled={!selectedId || importing || loading}
          >
            {importing ? '가져오는 중...' : '가져오기'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClassScheduleLeaderSectionImportModal;
